/**
 * Floating Timer Component
 *
 * A draggable floating timer that can be moved anywhere on screen.
 * Features: start/stop, reset, adjust time (minutes/seconds), transparency.
 * Persists position and settings in localStorage.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { Play, Pause, RotateCcw, GripVertical, ChevronUp, ChevronDown, Minimize2, Eye, Maximize2, X } from 'lucide-react';

interface FloatingTimerProps {
  /** Called when the timer is minimized/closed */
  onMinimize: () => void;
  /** Initial visibility state */
  isVisible: boolean;
}

const STORAGE_KEY = 'floating-timer-state';

// Widget dimensions for bounds checking
const WIDGET_WIDTH = 200;
const WIDGET_HEIGHT = 250;
const COMPACT_WIDTH = 160;
const COMPACT_HEIGHT = 36;

interface TimerState {
  positionX: number;
  positionY: number;
  lastDuration: number; // in seconds
  opacity: number; // 0.3 to 1.0
  isCompact: boolean; // compact mode: shows only time remaining in a small bar
}

/**
 * Clamp position to keep widget visible on screen
 * Ensures at least 50px of the widget is visible
 */
function clampPosition(x: number, y: number): { x: number; y: number } {
  const minVisible = 50;
  const maxX = window.innerWidth - minVisible;
  const maxY = window.innerHeight - minVisible;

  return {
    x: Math.max(-WIDGET_WIDTH + minVisible, Math.min(maxX, x)),
    y: Math.max(0, Math.min(maxY, y)),
  };
}

function loadTimerState(): TimerState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Clamp position to ensure widget is visible (fixes disappearing issue)
      const { x, y } = clampPosition(parsed.positionX ?? 0, parsed.positionY ?? 0);
      return {
        positionX: x,
        positionY: y,
        lastDuration: parsed.lastDuration ?? 5 * 60,
        opacity: parsed.opacity ?? 1.0,
        isCompact: parsed.isCompact ?? false,
      };
    }
  } catch (error) {
    console.error('[FloatingTimer] Failed to load state:', error);
  }
  return {
    positionX: Math.max(0, window.innerWidth - 220),
    // Position below typical header areas (navbar ~60px + page header ~80px + margin)
    // to avoid overlapping with Edit/Delete buttons in detail views
    positionY: 350,
    lastDuration: 5 * 60, // 5 minutes default
    opacity: 1.0,
    isCompact: false,
  };
}

function saveTimerState(state: Partial<TimerState>) {
  try {
    const current = loadTimerState();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...state }));
  } catch (error) {
    console.error('[FloatingTimer] Failed to save state:', error);
  }
}

export const FloatingTimer: React.FC<FloatingTimerProps> = ({ onMinimize, isVisible }) => {
  const [state, setState] = useState<TimerState>(loadTimerState);
  const [timeLeft, setTimeLeft] = useState(state.lastDuration);
  const [isRunning, setIsRunning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editMinutes, setEditMinutes] = useState(Math.floor(state.lastDuration / 60));
  const [editSeconds, setEditSeconds] = useState(state.lastDuration % 60);
  const [isDragging, setIsDragging] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const constraintsRef = useRef<HTMLDivElement>(null);

  // Use motion values for position - this prevents the transform accumulation bug
  // where CSS left/top + transform translate causes double-counting on drag end
  const motionX = useMotionValue(state.positionX);
  const motionY = useMotionValue(state.positionY);

  // Sync motion values when state changes (e.g., on load or window resize)
  // Only sync when NOT dragging to avoid interrupting active drags
  useEffect(() => {
    if (!isDragging) {
      motionX.set(state.positionX);
      motionY.set(state.positionY);
    }
  }, [state.positionX, state.positionY, isDragging, motionX, motionY]);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer logic
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            // Play notification sound or visual alert
            playAlarm();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeLeft]);

  // Play alarm when timer finishes
  const playAlarm = useCallback(() => {
    // Flash the timer
    const flash = () => {
      document.title = document.title === '⏰ Timer Done!' ? '🔔 Time\'s Up!' : '⏰ Timer Done!';
    };
    const flashInterval = setInterval(flash, 500);

    // Reset after 5 seconds
    setTimeout(() => {
      clearInterval(flashInterval);
      document.title = 'Benefits Outreach 360';
    }, 5000);

    // Try to play a sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;

      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 300);
    } catch (e) {
      // Audio not available
    }
  }, []);

  const handleStartStop = () => {
    if (timeLeft === 0) {
      // Reset if timer finished
      setTimeLeft(state.lastDuration);
    }
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setIsRunning(false);
    setTimeLeft(state.lastDuration);
  };

  const handleAdjustTime = (minutesDelta: number, secondsDelta: number) => {
    if (isRunning) return;

    const newTime = Math.max(0, Math.min(5999, timeLeft + minutesDelta * 60 + secondsDelta));
    setTimeLeft(newTime);
    saveTimerState({ lastDuration: newTime });
    setState(prev => ({ ...prev, lastDuration: newTime }));
  };

  const handleSetTime = () => {
    const newTime = Math.max(0, Math.min(5999, editMinutes * 60 + editSeconds));
    setTimeLeft(newTime);
    saveTimerState({ lastDuration: newTime });
    setState(prev => ({ ...prev, lastDuration: newTime }));
    setIsEditing(false);
  };

  // Handle drag start - set dragging state
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  // Handle drag end - read final position from motion values
  // Motion values already contain the correct absolute position after drag
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);

    // Read current position from motion values and clamp
    const rawX = motionX.get();
    const rawY = motionY.get();
    const { x: newX, y: newY } = clampPosition(rawX, rawY);

    // Update state and persist
    setState(prev => ({ ...prev, positionX: newX, positionY: newY }));
    saveTimerState({ positionX: newX, positionY: newY });

    // Sync motion values with clamped position
    motionX.set(newX);
    motionY.set(newY);
  }, [motionX, motionY]);

  // Handle opacity change
  const handleOpacityChange = (newOpacity: number) => {
    const clamped = Math.max(0.3, Math.min(1.0, newOpacity));
    setState(prev => ({ ...prev, opacity: clamped }));
    saveTimerState({ opacity: clamped });
  };

  // Toggle compact mode
  const handleToggleCompact = useCallback(() => {
    setState(prev => {
      const newCompact = !prev.isCompact;
      saveTimerState({ isCompact: newCompact });
      return { ...prev, isCompact: newCompact };
    });
  }, []);

  // Re-clamp position on window resize to keep timer visible
  useEffect(() => {
    const handleResize = () => {
      setState(prev => {
        const { x, y } = clampPosition(prev.positionX, prev.positionY);
        if (x !== prev.positionX || y !== prev.positionY) {
          saveTimerState({ positionX: x, positionY: y });
          return { ...prev, positionX: x, positionY: y };
        }
        return prev;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle opacity quick preset
  const [showOpacitySlider, setShowOpacitySlider] = useState(false);

  // Calculate progress for circular indicator
  const progress = state.lastDuration > 0 ? ((state.lastDuration - timeLeft) / state.lastDuration) * 100 : 0;
  const isComplete = timeLeft === 0 && !isRunning;

  if (!isVisible) return null;

  return (
    <>
      {/* Drag constraints reference - covers full viewport for free movement */}
      <div
        ref={constraintsRef}
        className="fixed inset-0 pointer-events-none"
        aria-hidden="true"
      />
      <AnimatePresence>
        <motion.div
          className="fixed z-50"
          style={{
            // Position using CSS left/top directly (not transforms) so hit-testing works correctly
            // This ensures Playwright and browser hit-tests see the element at its visual position
            left: motionX,
            top: motionY,
            opacity: state.opacity,
          }}
          drag
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={constraintsRef}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: state.opacity, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        >
          {/* Compact View - just shows time remaining in a small bar */}
          {state.isCompact ? (
            <motion.div
              className={`
                rounded-lg shadow-xl border backdrop-blur-sm cursor-move
                ${isComplete
                  ? 'bg-amber-500 border-amber-400 animate-pulse'
                  : 'bg-dark-surface border-dark-border/30'
                }
              `}
              layout
            >
              <div className={`
                flex items-center gap-2 px-3 py-1.5
                ${isComplete ? 'text-white' : 'text-dark-text'}
              `}>
                <GripVertical size={12} className={isComplete ? 'text-amber-100' : 'text-dark-muted'} />
                <span className="text-xs font-medium">Timer</span>
                <span className={`
                  font-mono text-sm font-bold tabular-nums
                  ${isComplete ? 'text-white' : isRunning ? 'text-green-400' : 'text-dark-text'}
                `}>
                  {formatTime(timeLeft)}
                </span>
                <div className="flex items-center gap-0.5 ml-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleStartStop(); }}
                    className={`p-1 rounded transition-all duration-150 ${
                      isRunning
                        ? 'text-yellow-400 hover:bg-yellow-500/20'
                        : isComplete
                          ? 'text-white hover:bg-amber-400/30'
                          : 'text-brand-400 hover:bg-brand-500/20'
                    }`}
                    title={isRunning ? 'Pause' : 'Start'}
                  >
                    {isRunning ? <Pause size={12} /> : <Play size={12} />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleCompact(); }}
                    className={`p-1 rounded transition-all duration-150 ${
                      isComplete ? 'hover:bg-amber-400/30 text-white' : 'hover:bg-dark-surface/40 text-dark-muted'
                    }`}
                    title="Expand timer"
                  >
                    <Maximize2 size={12} />
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
          /* Full View */
          <motion.div
            className={`
              w-48 rounded-xl shadow-2xl border backdrop-blur-sm
              ${isComplete
                ? 'bg-amber-500 border-amber-400 animate-pulse'
                : 'bg-dark-surface border-dark-border/30'
              }
            `}
            layout
          >
            {/* Header */}
            <div className={`
              flex items-center justify-between px-3 py-2 rounded-t-xl cursor-move
              ${isComplete ? 'text-white' : 'text-dark-text'}
            `}>
              <div className="flex items-center gap-2">
                <GripVertical size={14} className={isComplete ? 'text-amber-100' : 'text-dark-muted'} />
                <span className="text-xs font-medium">Timer</span>
              </div>
              <div className="flex items-center gap-1">
                {/* Opacity Toggle */}
                <button
                  onClick={() => setShowOpacitySlider(!showOpacitySlider)}
                  className={`p-1 rounded transition-all duration-150 ${
                    showOpacitySlider
                      ? 'bg-brand-500/30 text-brand-400'
                      : isComplete
                        ? 'hover:bg-amber-400/30 text-white'
                        : 'hover:bg-dark-surface/40'
                  }`}
                  title="Adjust transparency"
                >
                  <Eye size={12} />
                </button>
                {/* Compact mode toggle */}
                <button
                  onClick={handleToggleCompact}
                  className={`p-1 rounded transition-all duration-150 ${
                    isComplete ? 'hover:bg-amber-400/30 text-white' : 'hover:bg-dark-surface/40'
                  }`}
                  title="Shrink to compact bar"
                >
                  <Minimize2 size={12} />
                </button>
                {/* Hide timer completely */}
                <button
                  onClick={onMinimize}
                  className={`p-1 rounded transition-all duration-150 ${
                    isComplete ? 'hover:bg-amber-400/30 text-white' : 'hover:bg-dark-surface/40 text-dark-muted hover:text-red-400'
                  }`}
                  title="Hide timer"
                >
                  <X size={12} />
                </button>
            </div>
            </div>

            {/* Opacity Slider */}
            {showOpacitySlider && (
              <div className="px-3 py-2 border-t border-dark-border/30">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-dark-muted">Opacity</span>
                  <input
                    type="range"
                    min="30"
                    max="100"
                    value={state.opacity * 100}
                    onChange={(e) => handleOpacityChange(parseInt(e.target.value) / 100)}
                    className="flex-1 h-1 bg-dark-border rounded-lg appearance-none cursor-pointer accent-brand-500"
                  />
                  <span className="text-[10px] text-dark-muted w-6 text-right">{Math.round(state.opacity * 100)}%</span>
                </div>
              </div>
            )}

              {/* Timer Display */}
              <div className="px-4 py-3">
                {isEditing ? (
                  /* Edit Mode */
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        max="99"
                        value={editMinutes}
                        onChange={(e) => setEditMinutes(Math.max(0, Math.min(99, parseInt(e.target.value) || 0)))}
                        className="w-12 text-center text-lg font-mono font-bold bg-dark-bg border border-dark-border/30 rounded px-1 py-1 text-dark-text"
                      />
                      <span className={`text-xl font-bold ${isComplete ? 'text-white' : 'text-dark-text'}`}>:</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={editSeconds}
                        onChange={(e) => setEditSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                        className="w-12 text-center text-lg font-mono font-bold bg-dark-bg border border-dark-border/30 rounded px-1 py-1 text-dark-text"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSetTime}
                        className="px-3 py-1 text-xs font-medium bg-brand-500 text-white rounded hover:bg-brand-600 transition-all duration-150"
                      >
                        Set
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-3 py-1 text-xs font-medium bg-dark-border text-dark-text rounded hover:bg-dark-surface/40 transition-all duration-150"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <div className="relative flex flex-col items-center">
                    {/* Circular Progress */}
                    <div className="relative w-24 h-24">
                      <svg className="w-full h-full -rotate-90">
                        <circle
                          cx="48"
                          cy="48"
                          r="42"
                          fill="none"
                          stroke={isComplete ? 'rgba(255,255,255,0.3)' : 'rgba(6,182,212,0.2)'}
                          strokeWidth="6"
                        />
                        <circle
                          cx="48"
                          cy="48"
                          r="42"
                          fill="none"
                          stroke={isComplete ? '#fff' : isRunning ? '#22c55e' : '#06b6d4'}
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 42}`}
                          strokeDashoffset={`${2 * Math.PI * 42 * (1 - progress / 100)}`}
                          className="transition-all duration-200"
                        />
                      </svg>

                      {/* Time Display */}
                      <button
                        onClick={() => !isRunning && setIsEditing(true)}
                        disabled={isRunning}
                        className={`
                          absolute inset-0 flex items-center justify-center
                          font-mono text-2xl font-bold tabular-nums
                          ${isComplete ? 'text-white' : 'text-dark-text'}
                          ${!isRunning ? 'hover:text-brand-500 cursor-pointer' : 'cursor-default'}
                          transition-all duration-150
                        `}
                        title={isRunning ? '' : 'Click to edit'}
                      >
                        {formatTime(timeLeft)}
                      </button>
                    </div>

                    {/* Quick Adjust Buttons */}
                    {!isRunning && (
                      <div className="flex items-center justify-center gap-4 mt-2">
                        <div className="flex flex-col items-center">
                          <button
                            onClick={() => handleAdjustTime(1, 0)}
                            className="p-0.5 text-dark-muted hover:text-brand-500 transition-all duration-150"
                            title="+1 min"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <span className="text-[10px] text-dark-muted">min</span>
                          <button
                            onClick={() => handleAdjustTime(-1, 0)}
                            className="p-0.5 text-dark-muted hover:text-brand-500 transition-all duration-150"
                            title="-1 min"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                        <div className="flex flex-col items-center">
                          <button
                            onClick={() => handleAdjustTime(0, 10)}
                            className="p-0.5 text-dark-muted hover:text-brand-500 transition-all duration-150"
                            title="+10 sec"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <span className="text-[10px] text-dark-muted">sec</span>
                          <button
                            onClick={() => handleAdjustTime(0, -10)}
                            className="p-0.5 text-dark-muted hover:text-brand-500 transition-all duration-150"
                            title="-10 sec"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-2 px-4 pb-3">
                <motion.button
                  onClick={handleStartStop}
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-full
                    transition-all duration-150 shadow-md
                    ${isRunning
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                      : isComplete
                        ? 'bg-white hover:bg-gray-100 text-amber-600'
                        : 'bg-brand-500 hover:bg-brand-600 text-white'
                    }
                  `}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title={isRunning ? 'Pause' : 'Start'}
                >
                  {isRunning ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                </motion.button>

                <motion.button
                  onClick={handleReset}
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full
                    border transition-all duration-150
                    ${isComplete
                      ? 'border-amber-100/50 text-white hover:bg-amber-400/30'
                      : 'border-dark-border/30 text-dark-muted hover:text-brand-500 hover:border-brand-500/50'
                    }
                  `}
                  whileHover={{ scale: 1.1, rotate: -180 }}
                  whileTap={{ scale: 0.9 }}
                  title="Reset"
                >
                  <RotateCcw size={14} />
                </motion.button>
              </div>
          </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default FloatingTimer;
